/**
 * Phase 20 wizard — Step 2: space measurements (flexible entry).
 *
 * The customer picks HOW they want to measure — a wall surface (width ×
 * height), a floor area (square footage), or a linear run (feet) — and
 * enters it. The shell turns the choice into a context-carrying natural-
 * language turn; the Phase 20 synthesizer routes wall → project_wall_sqft,
 * floor → project_floor_sqft, linear → linear_ft. Invalid input surfaces
 * a gentle correction and sends nothing (FR-010, no fabrication).
 *
 * For whole rooms or irregular spaces that don't reduce to one of these,
 * "Let Stella help me measure" hands off to the chat with context; when
 * Stella records the measurement the wizard picks it up via the settle-
 * refetch and advances.
 */

import { useState, type FormEvent } from "react";

import type { DimensionSummaryItem } from "@starz/shared/wizard";

export type WizardMeasurement =
  | { kind: "wall"; widthFt: number; heightFt: number }
  | { kind: "floor"; areaSqft: number }
  | { kind: "linear"; lengthFt: number };

type MeasureKind = WizardMeasurement["kind"];

interface Props {
  /** Human label for the space, e.g. "bathroom" — used in copy. */
  spaceLabel?: string | null;
  onSubmit: (measurement: WizardMeasurement) => void;
  /** Hand off to Stella for whole rooms / irregular spaces. */
  onMeasureWithStella: () => void;
  /** Measurements already captured (e.g. from a room scan) — shown so the
   * customer can confirm and continue instead of re-entering. */
  captured?: DimensionSummaryItem[];
  /** Pre-fill when the customer navigates Back to this step (read-back). */
  initial?: WizardMeasurement | null;
  disabled?: boolean;
}

const MODES: { key: MeasureKind; label: string; hint: string }[] = [
  { key: "wall", label: "A wall", hint: "Width × height" },
  { key: "floor", label: "Floor area", hint: "Square footage" },
  { key: "linear", label: "Length", hint: "Linear feet" },
];

const inputClass =
  "w-full rounded-lg border border-starz-gold/20 bg-white px-4 py-3 text-lg font-semibold text-starz-navy shadow-inner focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40 disabled:opacity-60";
const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray";

export function DimensionsStep({
  spaceLabel,
  onSubmit,
  onMeasureWithStella,
  captured = [],
  initial = null,
  disabled = false,
}: Props) {
  const [kind, setKind] = useState<MeasureKind>(initial?.kind ?? "wall");
  const [width, setWidth] = useState(initial?.kind === "wall" ? String(initial.widthFt) : "");
  const [height, setHeight] = useState(initial?.kind === "wall" ? String(initial.heightFt) : "");
  const [area, setArea] = useState(initial?.kind === "floor" ? String(initial.areaSqft) : "");
  const [length, setLength] = useState(initial?.kind === "linear" ? String(initial.lengthFt) : "");
  const [error, setError] = useState<string | null>(null);

  function positive(...vals: string[]): number[] | null {
    const nums = vals.map(Number);
    if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return null;
    return nums;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (disabled) return;
    if (kind === "wall") {
      const ok = positive(width, height);
      if (!ok) return setError("Enter a width and height greater than zero.");
      setError(null);
      return onSubmit({ kind: "wall", widthFt: ok[0], heightFt: ok[1] });
    }
    if (kind === "floor") {
      const ok = positive(area);
      if (!ok) return setError("Enter a floor area greater than zero.");
      setError(null);
      return onSubmit({ kind: "floor", areaSqft: ok[0] });
    }
    const ok = positive(length);
    if (!ok) return setError("Enter a length greater than zero.");
    setError(null);
    return onSubmit({ kind: "linear", lengthFt: ok[0] });
  }

  const hasCaptured = captured.length > 0;

  return (
    <form onSubmit={submit} className="space-y-4">
      {hasCaptured ? (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
            ✓ Measurements captured
          </p>
          <dl className="mt-2 space-y-1">
            {captured.map((c) => (
              <div key={c.label} className="flex justify-between text-sm">
                <dt className="text-warm-gray">{c.label}</dt>
                <dd className="font-semibold text-starz-navy">{c.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-warm-gray">
            Tap <span className="font-semibold text-starz-navy">Continue</span> below to keep these,
            or enter different measurements.
          </p>
        </section>
      ) : (
        <p className="text-sm text-warm-gray">
          How would you like to measure {spaceLabel ? `your ${spaceLabel}` : "the space"}? A rough
          measurement is fine — we'll confirm on site.
        </p>
      )}

      {/* Measurement-type selector */}
      <div className="grid grid-cols-3 gap-2" role="group" aria-label="What to measure">
        {MODES.map((m) => {
          const selected = kind === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => {
                setKind(m.key);
                setError(null);
              }}
              disabled={disabled}
              aria-pressed={selected}
              className={
                "rounded-lg border-2 px-2 py-3 text-center transition-colors disabled:opacity-60 " +
                (selected
                  ? "border-starz-gold bg-starz-gold/10"
                  : "border-starz-gold/15 hover:border-starz-gold/40")
              }
            >
              <span className="block text-sm font-semibold text-starz-navy">{m.label}</span>
              <span className="block text-xs text-warm-gray">{m.hint}</span>
            </button>
          );
        })}
      </div>

      {kind === "wall" ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="wizard-width" className={labelClass}>
              Width (ft)
            </label>
            <input
              id="wizard-width"
              inputMode="decimal"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              disabled={disabled}
              className={inputClass}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="wizard-height" className={labelClass}>
              Height (ft)
            </label>
            <input
              id="wizard-height"
              inputMode="decimal"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              disabled={disabled}
              className={inputClass}
            />
          </div>
        </div>
      ) : kind === "floor" ? (
        <div>
          <label htmlFor="wizard-area" className={labelClass}>
            Floor area (sq ft)
          </label>
          <input
            id="wizard-area"
            inputMode="decimal"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            disabled={disabled}
            className={inputClass}
          />
        </div>
      ) : (
        <div>
          <label htmlFor="wizard-length" className={labelClass}>
            Length (linear ft)
          </label>
          <input
            id="wizard-length"
            inputMode="decimal"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            disabled={disabled}
            className={inputClass}
          />
        </div>
      )}

      {error ? (
        <p role="alert" className="text-sm text-rose-700">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={disabled}
        className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Continue
      </button>

      <div className="border-t border-starz-gold/15 pt-4">
        <p className="text-sm text-warm-gray">
          Whole room, or an irregular space that doesn't fit the above?
        </p>
        <button
          type="button"
          onClick={onMeasureWithStella}
          disabled={disabled}
          className="mt-2 min-h-[44px] rounded-lg border border-starz-gold/40 px-5 py-3 text-sm font-semibold text-starz-navy transition-colors hover:bg-starz-cream disabled:opacity-40"
        >
          💬 Let Stella help me measure
        </button>
      </div>
    </form>
  );
}
